import { useEffect, useRef, useState } from "react";
import { useNetworkStatus } from "../../../hooks/useNetworkStatus";

import { useTask } from "../../../hooks/query";
import { useNotification } from "../../../hooks";
import { ImageWithMetadata } from "../../../constants/inspectionPhotos";
import { offlineStore } from "../../../utils/offlineStore";
import {
    isBlobImageUrl,
    mapUploadResponseToImage,
    toPreviewImage,
} from "../../../utils/taskAssetImages";

interface UseSingleAlbumOptions {
    label: string;
    taskId: string;
    projectId: string;
    images: ImageWithMetadata[];
    onImagesChange: (images: ImageWithMetadata[]) => void;
}

export function useSingleAlbum(props: UseSingleAlbumOptions) {
    const { label, taskId, projectId, images, onImagesChange } = props;
    const { useUploadAsset, useDeleteAsset } = useTask();
    const { mutateAsync: uploadAsset, isPending: isUploading } = useUploadAsset();
    const { mutateAsync: deleteAsset, isPending: isDeleting } = useDeleteAsset();
    const { showError, showSuccess } = useNotification();
    const isOnline = useNetworkStatus();
    const imagesRef = useRef(images);
    imagesRef.current = images;

    const [selectedImage, setSelectedImage] = useState<ImageWithMetadata | null>(null);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const cleanupBlobUrls = () => {
        images.forEach((image) => {
            if (isBlobImageUrl(image.url)) {
                URL.revokeObjectURL(image.url);
            }
        });
    };

    useEffect(() => {
        return cleanupBlobUrls;
    }, [images]);

    const uploadFiles = async (files: File[], note = "") => {
        let ok = 0;
        let fail = 0;
        let nextImages = [...imagesRef.current];

        for (const file of files) {
            try {
                const data = new FormData();
                data.append("File", file);
                data.append("Label", label);
                data.append("TaskId", taskId);
                data.append("FileName", file.name);
                data.append("ProjectId", projectId);
                if (note) data.append("Note", note);

                if (!isOnline || taskId.startsWith("offline-") || projectId.startsWith("offline-")) {
                    const tempId = `offline-img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                    await offlineStore.saveOfflinePhoto({
                        offlineId: tempId,
                        taskId,
                        projectId,
                        label,
                        file,
                        fileName: file.name,
                        note,
                        timestamp: Date.now()
                    });
                    
                    const uploadedImage: ImageWithMetadata = {
                        url: URL.createObjectURL(file),
                        assetId: tempId,
                        note: note || null,
                    };
                    
                    const blobIndex = nextImages.findIndex(
                        (img) => isBlobImageUrl(img.url) && !img.assetId,
                    );

                    if (blobIndex === -1) {
                        nextImages = [...nextImages, uploadedImage];
                    } else {
                        URL.revokeObjectURL(nextImages[blobIndex].url);
                        nextImages = [...nextImages];
                        nextImages[blobIndex] = uploadedImage;
                    }
                    ok++;
                    continue;
                }

                const response = await uploadAsset(data);
                const uploadedImage = mapUploadResponseToImage(response, note);
                const blobIndex = nextImages.findIndex(
                    (img) => isBlobImageUrl(img.url) && !img.assetId,
                );

                if (blobIndex === -1) {
                    nextImages = [...nextImages, uploadedImage];
                } else {
                    URL.revokeObjectURL(nextImages[blobIndex].url);
                    nextImages = [...nextImages];
                    nextImages[blobIndex] = uploadedImage;
                }

                ok++;
            } catch {
                fail++;
            }
        }

        imagesRef.current = nextImages;
        onImagesChange(nextImages);

        if (ok > 0) {
            showSuccess(ok === 1 ? "Image uploaded" : `${ok} images uploaded`);
        }
        if (fail > 0) {
            showError(`Failed to upload ${fail} image(s)`);
        }
    };

    const openFilePicker = () => {
        if (fileInputRef.current) fileInputRef.current.value = "";
        setTimeout(() => fileInputRef.current?.click(), 10);
    };

    const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (files.length > 0) {
            setPendingFiles(files);
            setUploadModalOpen(true);
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleUploadConfirm = async (files: File[], note: string) => {
        const previews = files.map((file) => ({
            url: URL.createObjectURL(file),
            note: note || null,
        }));
        const nextImages = [...imagesRef.current, ...previews];
        imagesRef.current = nextImages;
        onImagesChange(nextImages);
        await uploadFiles(files, note);
    };

    const openPreview = (image: ImageWithMetadata | string) => {
        if (typeof image === "string") {
            setSelectedImage({ url: image });
            return;
        }
        setSelectedImage(toPreviewImage(image));
    };

    const deleteImage = async (image?: ImageWithMetadata | null) => {
        const target = image || selectedImage;
        if (!target) return false;

        const confirmed = window.confirm(
            "Delete this photo? This cannot be undone.",
        );
        if (!confirmed) return false;

        const assetId = target.assetId;
        const matchKey = assetId || target.url;

        try {
            if (assetId?.startsWith("offline-img-")) {
                await offlineStore.removeOfflinePhoto(assetId);
            } else if (assetId && !isBlobImageUrl(target.url)) {
                if (!isOnline) {
                    showError("Connect to the internet to delete synced photos");
                    return false;
                }
                await deleteAsset({ AssetId: assetId });
            }

            if (isBlobImageUrl(target.url)) {
                URL.revokeObjectURL(target.url);
            }

            const nextImages = imagesRef.current.filter((img) => {
                if (assetId && img.assetId) {
                    return img.assetId !== assetId;
                }
                return img.url !== matchKey && img !== target;
            });
            imagesRef.current = nextImages;
            onImagesChange(nextImages);
            setSelectedImage(null);
            showSuccess("Photo deleted");
            return true;
        } catch (e) {
            console.error(e);
            showError("Failed to delete photo");
            return false;
        }
    };

    return {
        selectedImage,
        closePreview: () => setSelectedImage(null),
        openPreview,
        deleteImage,
        isDeleting,
        uploadModalOpen,
        setUploadModalOpen,
        pendingFiles,
        setPendingFiles,
        fileInputRef,
        openFilePicker,
        handleFileInput,
        handleUploadConfirm,
        isUploading,
    };
}
